/**
 * /gift — Public gift subscription purchase flow.
 * No Mori shell or nav — standalone marketing page.
 *
 * Steps:
 *  1. Occasion picker
 *  2. Tier picker (Keeper / Storyteller)
 *  3. Details form
 *  4. Review & checkout
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GiftCardPreview } from "@/components/GiftCardPreview";
import { MARKETING_URL } from "@/lib/urls";
import { PublicShell } from "@/components/PublicShell";

// ── Types ────────────────────────────────────────────────────────────────────

type Occasion = "new_baby" | "birthday" | "holiday" | "general";
type Tier = "keeper";

interface GiftFormData {
  occasion: Occasion | null;
  tier: Tier | null;
  gifterName: string;
  gifterEmail: string;
  recipientName: string;
  recipientEmail: string;
  personalNote: string;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const OCCASIONS: Array<{ id: Occasion; emoji: string; label: string }> = [
  { id: "new_baby",  emoji: "🍼", label: "New baby / expecting" },
  { id: "birthday",  emoji: "🎂", label: "Birthday" },
  { id: "holiday",   emoji: "🎄", label: "Christmas / holiday" },
  { id: "general",   emoji: "🌰", label: "Just because" },
];

const TIERS: Array<{
  id: Tier;
  name: string;
  price: string;
  badge: string;
  features: string[];
}> = [
  {
    id: "keeper",
    name: "Plus",
    price: "€41.58",
    badge: "30% off",
    features: [
      "Unlimited memories",
      "Family sharing",
      "Monthly story from Mori",
    ],
  },
];

const OCCASION_LABELS: Record<Occasion, string> = {
  new_baby:  "New baby / expecting",
  birthday:  "Birthday",
  holiday:   "Christmas / holiday",
  general:   "Just because",
};

// ── Page ──────────────────────────────────────────────────────────────────────

export default function GiftPage() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<GiftFormData>({
    occasion: null,
    tier: null,
    gifterName: "",
    gifterEmail: "",
    recipientName: "",
    recipientEmail: "",
    personalNote: "",
  });

  const update = (patch: Partial<GiftFormData>) =>
    setForm((f) => ({ ...f, ...patch }));

  const handleCheckout = async () => {
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("gift-checkout", {
        body: {
          tier:               form.tier,
          occasion:           form.occasion,
          gifter_name:        form.gifterName.trim(),
          gifter_email:       form.gifterEmail.trim().toLowerCase(),
          recipient_email:    form.recipientEmail.trim().toLowerCase() || undefined,
          personal_note:      form.personalNote.trim() || undefined,
          lang:               i18n.language,
        },
      });
      if (error || !data?.checkoutUrl) throw new Error("Could not start checkout");
      // Persist card data so GiftSuccessPage can render the downloadable card
      sessionStorage.setItem("mori_gift_card_data", JSON.stringify({
        recipientName: form.recipientName.trim(),
        gifterName:    form.gifterName.trim(),
        personalNote:  form.personalNote.trim(),
        occasion:      form.occasion,
        tier:          form.tier,
      }));
      window.location.href = data.checkoutUrl as string;
    } catch {
      toast.error(t("error.gift.checkoutFailed"));
      setSubmitting(false);
    }
  };

  const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
  const gifterEmailErr   = form.gifterEmail    && !isValidEmail(form.gifterEmail)   ? "Enter a valid email address" : null;
  const recipientEmailErr = form.recipientEmail && !isValidEmail(form.recipientEmail) ? "Enter a valid email address"
    : form.recipientEmail && form.recipientEmail.trim().toLowerCase() === form.gifterEmail.trim().toLowerCase() ? "Recipient can't be the same as you"
    : null;

  return (
    <PublicShell>
      <Toaster position="top-center" />
    <div className="flex flex-col min-h-full">

      {/* Header */}
      <header className="flex items-center justify-between px-5 py-4 border-b border-border/50 sticky top-0 bg-background/95 backdrop-blur-sm z-10">
        {step > 1 ? (
          <button
            type="button"
            onClick={() => setStep((s) => s - 1)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
        ) : (
          <span />
        )}
        <span className="text-base font-semibold text-foreground">
          🌰 Give a gift
        </span>
        {/* Step dots */}
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: s === step ? 16 : 6,
                backgroundColor: s <= step ? "#7A9A7C" : "#E0E0D8",
              }}
            />
          ))}
        </div>
      </header>

      <main className="flex-1 max-w-lg mx-auto w-full px-5 py-8 space-y-6">

        {/* ── Step 1: Occasion ─────────────────────────────────────────────── */}
        {step === 1 && (
          <>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-foreground">What's the occasion?</h1>
              <p className="text-sm text-muted-foreground">Mori will carry the right feeling.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {OCCASIONS.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { update({ occasion: o.id }); setStep(2); }}
                  className="flex flex-col items-center gap-3 rounded-2xl border bg-card p-6 text-center transition-all hover:border-primary/60 hover:shadow-sm active:scale-[0.98]"
                  style={{ borderColor: form.occasion === o.id ? "#7A9A7C" : undefined }}
                >
                  <span className="text-4xl leading-none">{o.emoji}</span>
                  <span className="text-sm font-medium text-foreground leading-tight">{o.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ── Step 2: Tier ────────────────────────────────────────────────── */}
        {step === 2 && (
          <>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-foreground">{t("gift.chooseGift")}</h1>
              <p className="text-sm text-muted-foreground">One year of Mori — a full satchel of memories.</p>
            </div>
            <div className="space-y-3">
              {TIERS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => { update({ tier: t.id }); setStep(3); }}
                  className="w-full rounded-2xl border bg-card p-5 text-left transition-all hover:border-primary/60 hover:shadow-sm active:scale-[0.98]"
                  style={{ borderColor: form.tier === t.id ? "#7A9A7C" : undefined, borderWidth: form.tier === t.id ? 2 : 1 }}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="text-lg font-semibold text-foreground">{t.name}</h3>
                    <span
                      className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider shrink-0"
                      style={{ backgroundColor: "#FDF0EE", color: "#8B3A2E" }}
                    >
                      {t.badge}
                    </span>
                  </div>
                  <ul className="space-y-1 mb-4">
                    {t.features.map((f) => (
                      <li key={f} className="text-sm text-foreground/80">✓ {f}</li>
                    ))}
                  </ul>
                  <p className="text-2xl font-bold text-foreground">{t.price} <span className="text-sm font-normal text-muted-foreground">/ year gift</span></p>
                </button>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground text-center">
              Secure payment via Stripe. meetmori.app is a web purchase.
            </p>
          </>
        )}

        {/* ── Step 3: Details ──────────────────────────────────────────────── */}
        {step === 3 && (
          <>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-foreground">{t("gift.fewDetails")}</h1>
              <p className="text-sm text-muted-foreground">Mori needs to know who to thank and who to surprise.</p>
            </div>
            <div className="space-y-4">
              <Field label="Your name" required>
                <input
                  type="text"
                  placeholder="Ondrej"
                  value={form.gifterName}
                  onChange={(e) => update({ gifterName: e.target.value })}
                  className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </Field>
              <Field label="Your email" required error={gifterEmailErr}>
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={form.gifterEmail}
                  onChange={(e) => update({ gifterEmail: e.target.value })}
                  className={`w-full rounded-xl border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 ${gifterEmailErr ? "border-destructive" : "border-border"}`}
                />
              </Field>
              <Field label="Recipient's name" required>
                <input
                  type="text"
                  placeholder="Emma"
                  value={form.recipientName}
                  onChange={(e) => update({ recipientName: e.target.value })}
                  className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </Field>
              <Field label="Recipient's email" hint="Optional · You can share the gift link yourself after checkout" error={recipientEmailErr}>
                <input
                  type="email"
                  placeholder="friend@example.com"
                  value={form.recipientEmail}
                  onChange={(e) => update({ recipientEmail: e.target.value })}
                  className={`w-full rounded-xl border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 ${recipientEmailErr ? "border-destructive" : "border-border"}`}
                />
              </Field>
              <Field label={t("gift.personalNote")} hint="Optional · max 280 characters">
                <textarea
                  rows={4}
                  maxLength={280}
                  placeholder={t("gift.moriLoves")}
                  value={form.personalNote}
                  onChange={(e) => update({ personalNote: e.target.value })}
                  className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none"
                />
                <p className="text-right text-[11px] text-muted-foreground mt-1">
                  {form.personalNote.length} / 280
                </p>
              </Field>
            </div>
            <Button
              className="w-full h-12"
              disabled={
                !form.gifterName.trim() ||
                !form.gifterEmail.trim() ||
                !form.recipientName.trim() ||
                !!gifterEmailErr ||
                !!recipientEmailErr
              }
              onClick={() => setStep(4)}
            >
              Review gift →
            </Button>

            {/* Live card preview — updates as user types */}
            {(form.recipientName || form.gifterName || form.personalNote) && (
              <div className="pt-4 space-y-2">
                <p className="text-[11px] text-muted-foreground text-center tracking-wide uppercase">Card preview</p>
                <GiftCardPreview
                  recipientName={form.recipientName}
                  gifterName={form.gifterName}
                  message={form.personalNote}
                  occasion={form.occasion}
                  tier={form.tier}
                  showDownload={false}
                />
              </div>
            )}
          </>
        )}

        {/* ── Step 4: Review & checkout ────────────────────────────────────── */}
        {step === 4 && form.occasion && form.tier && (
          <>
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold text-foreground">Ready to give?</h1>
              <p className="text-sm text-muted-foreground">Mori is waiting by the fire.</p>
            </div>

            {/* Card preview */}
            <GiftCardPreview
              recipientName={form.recipientName}
              gifterName={form.gifterName}
              message={form.personalNote}
              occasion={form.occasion}
              tier={form.tier}
              showDownload={false}
            />

            {/* Summary card */}
            <div className="rounded-2xl border border-border/60 bg-card p-5 space-y-4">
              <SummaryRow label="Occasion" value={OCCASION_LABELS[form.occasion]} />
              <SummaryRow
                label="Gift"
                value={`Mori ${TIERS.find((t) => t.id === form.tier)!.name} — 1 year`}
              />
              <SummaryRow label="To" value={form.recipientEmail ? `${form.recipientName} (${form.recipientEmail})` : form.recipientName} />
              {form.personalNote && (
                <div className="pt-1 border-t border-border/40">
                  <p className="text-[11px] text-muted-foreground mb-1">Your note</p>
                  <p className="text-sm text-foreground/80 italic leading-relaxed line-clamp-3">
                    {form.personalNote}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Button
                className="w-full h-12 text-base"
                onClick={handleCheckout}
                disabled={submitting}
              >
                {submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Starting checkout…</>
                ) : (
                  "Give this gift →"
                )}
              </Button>
              <p className="text-[11px] text-muted-foreground text-center leading-relaxed">
                Secure payment via Stripe. meetmori.app/gift is a web purchase.
              </p>
            </div>
          </>
        )}
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <a href={MARKETING_URL} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
          meetmori.app
        </a>
      </footer>
    </div>
    </PublicShell>
  );
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  required,
  error,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  error?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </label>
        {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
      </div>
      {children}
      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-foreground text-right">{value}</span>
    </div>
  );
}
